/**
 * Layout component for the /tools/* routes (enduser interface).
 * Provides a simple, clean header with user info and logout.
 */
import { useAuth } from '@/contexts/AuthContext';
import { LogOut, Wrench } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';

interface ToolsLayoutProps {
  children: React.ReactNode;
}

export function ToolsLayout({ children }: ToolsLayoutProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate({ to: '/login' });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Simple header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <div
              className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center cursor-pointer hover:scale-105 transition-transform"
              onClick={() => navigate({ to: '/tools' })}
            >
              <Wrench className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-lg font-semibold text-slate-900">
                Hallo, {user?.first_name || user?.username || 'User'}
              </p>
              <p className="text-sm text-slate-500">Ihre verfügbaren Werkzeuge</p>
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Abmelden</span>
          </button>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {children}
      </main>
    </div>
  );
}

export default ToolsLayout;
